import React, { useState, useEffect } from "react";
import {
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CompIcon from "../../svgs/collections/CompIcon";
import PartnersIcon from "../../svgs/collections/PartnersIcon";
import SubCatsIcon from "../../svgs/collections/SubCatsIcon";
import JobsIcon from "../../svgs/collections/JobsIcon";
import CasesIcon from "../../svgs/collections/CasesIcon";
import DevsIcon from "../../svgs/collections/DevsIcon";
import OnIcon from "../../svgs/collections/OnIcon";
import EditIcon from "../../svgs/collections/EditIcon";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Search, Add } from "@mui/icons-material";
import MergeIcon from "../../svgs/collections/MergeIcon";
import FilterIcon from "../../svgs/collections/FilterIcon";
import OffIcon from "../../svgs/collections/OffIcon";
import IndustryCardDetailed from "./IndustryCardDetailed";
import IndustryCardExpanded from "./IndustryCardExpanded";
import IndustryCardCompact from "./IndustryCardCompact";
import SpSelector from "../../globals/SpSelector";

const viewsPaging = {
  compact: [8, 12, 16, 32, 50, 64, 100, 128, 200, 256, 500, 512],
  detailed: [6, 12, 18, 30, 50, 60, 100, 120, 200, 240, 480, 500],
  expanded: [6, 12, 18, 30, 50, 60, 100, 120, 200, 240, 480, 500],
};

const SingleCollection = ({
  item,
  subItems,
  onExpand,
  loadingSub,
  type,
  updateSubCollectionShowStatus,
  onEditMain,
  onAddSubItem,
  onEditSubItem,
  onMoveSubItem,
  onMoveMainItem,
  setSubSearch,
  setSubStatusFilter,
  subSearchQueries,
  subStatusFilters,
  shouldAutoOpen = false,
  searchQuery,
}) => {
  const [isEnabled, setIsEnabled] = useState(item.show ?? true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedView, setSelectedView] = useState("compact");
  const [subItemsPage, setSubItemsPage] = useState(1);
  const [subItemsPerPage, setSubItemsPerPage] = useState(
    viewsPaging[selectedView][0]
  );
  const [isShowLoading, setIsShowLoading] = useState(false);

  // Get current search and filter values for this collection
  const currentSearchQuery = subSearchQueries?.[item.id] || "";
  const currentStatusFilter = subStatusFilters?.[item.id] || "all";

  useEffect(() => {
    setSubItemsPerPage(viewsPaging[selectedView][0]);
  }, [selectedView]);

  useEffect(() => {
    if (isOpen && !subItems) {
      onExpand();
    }
  }, [isOpen, subItems, onExpand]);

  // Auto-open first collection when user starts typing
  useEffect(() => {
    if (shouldAutoOpen && searchQuery.trim() && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldAutoOpen, searchQuery, isOpen]);

  // const toggleEnabled = () => {
  //   setIsEnabled(!isEnabled);
  //   // Here you would also call a function to update the status in Firestore
  // };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  // const toggleSubcategoryEnabled = (id) => {
  //   // In real implementation, this would update the specific subcategory
  //   console.log("Toggle subcategory:", id);
  // };

  // Handle search input change for sub-items
  const handleSubSearchChange = (e) => {
    if (setSubSearch) {
      setSubSearch(item.id, e.target.value);
    }
  };

  // Handle status filter change for sub-items
  const handleSubStatusChange = (status) => {
    if (setSubStatusFilter) {
      setSubStatusFilter(item.id, status);
    }
  };

  // Sub-items are already filtered by the hook, so we just use them directly
  const filteredSubItems = subItems || [];

  const handleSubItemsPageChange = (event, value) => {
    setSubItemsPage(value);
  };

  const handleSubItemsPerPageChange = (event) => {
    setSubItemsPerPage(event.target.value);
    setSubItemsPage(1);
  };

  const paginatedSubItems = filteredSubItems.slice(
    (subItemsPage - 1) * subItemsPerPage,
    subItemsPage * subItemsPerPage
  );

  const onShowClicked = async (item, fromSub = false) => {
    if (fromSub) {
      // For sub-items, we call the function from the hook which will update the DB
      // and the onSnapshot listener will update the state.
      try {
        setIsShowLoading(true);
        await updateSubCollectionShowStatus(item);
      } catch (error) {
        console.log(error);
      } finally {
        setIsShowLoading(false);
      }
      return;
    }

    // For main collection item
    try {
      setIsShowLoading(true);
      await item?.ref?.update({
        show: !item?.show,
      });
      setIsEnabled(!isEnabled);
    } catch (error) {
      console.log(error);
    } finally {
      setIsShowLoading(false);
    }
  };

  const handleEditClick = () => {
    if (onEditMain) {
      onEditMain(item);
    }
  };

  const handleAddSubItemClick = () => {
    if (onAddSubItem) {
      onAddSubItem(item);
    }
  };

  const handleEditSubItemClick = (subItem) => {
    if (onEditSubItem) {
      onEditSubItem(subItem, item);
    }
  };

  const handleMoveSubItemClick = (subItem) => {
    if (onMoveSubItem) {
      onMoveSubItem(subItem, item);
    }
  };

  const handleMoveMainItemClick = () => {
    if (onMoveMainItem) {
      onMoveMainItem(item);
    }
  };

  const statusOptions = [
    { value: "all", label: "All statuses" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  return (
    <div className={`colManagement_single ${!isEnabled ? "disabled" : ""}`}>
      <div className="colManagement_single__main">
        <div className="colLogo">
          {item?.logoStorage ? (
            <img src={item?.logoStorage} alt={item?.name} />
          ) : (
            <div className="colLogo__placeholder">
              <span>{item?.name?.charAt(0).toUpperCase()}</span>
            </div>
          )}
        </div>
        <div className="colText">
          <h4>{item.name || item.title}</h4>
          <p>
            {item.description ||
              "End-to-end development of responsive, high-performance websites and web applications using modern technologies and frameworks."}
          </p>
        </div>
        <div className="colStats">
          <div className="colStatsSingle">
            <CompIcon />
            <span>223 companies</span>
          </div>
          <div className="colStatsSingle">
            <PartnersIcon />
            <span>412 partners</span>
          </div>
          <div className="colStatsSingle">
            <SubCatsIcon />
            <span>389 sub categories</span>
          </div>
          <div className="colStatsSingle">
            <JobsIcon />
            <span>254 jobs</span>
          </div>
          <div className="colStatsSingle">
            <CasesIcon />
            <span>313 case studies</span>
          </div>
          <div className="colStatsSingle">
            <DevsIcon />
            <span>278 devs</span>
          </div>
        </div>
        <div className="colActions">
          <button
            className="colActions__btn"
            onClick={() => onShowClicked(item)}
            disabled={isShowLoading}
          >
            {isEnabled ? <OnIcon /> : <OffIcon />}
            <span>{isEnabled ? "Enabled" : "Disabled"}</span>
          </button>
          <button
            className="colActions__btn"
            onClick={handleEditClick}
            disabled={isShowLoading}
          >
            <EditIcon />
            <span>Edit</span>
          </button>
          <button
            className="colActions__btn"
            onClick={handleMoveMainItemClick}
            disabled={isShowLoading}
          >
            <MergeIcon />
            <span>Move</span>
          </button>
        </div>
        <div className="colCloser">
          <button className="colCloser__btn" onClick={toggleOpen}>
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="colManagement_single__subCategories">
          <div className="subCategories__header">
            <div className="subCategories__search">
              <Search />
              <input
                type="text"
                placeholder={`Search ${
                  type === "stack"
                    ? "Technologies"
                    : type === "sector"
                    ? "Sub-Sectors"
                    : "Sub-Collections"
                }`}
                value={currentSearchQuery}
                onChange={handleSubSearchChange}
              />
            </div>

            <div className="subCategories__filters">
              <div className="status-filter">
                <SpSelector
                  className="status-dropdown"
                  renderHeader={() => (
                    <button className="status-dropdown">
                      <span>
                        {statusOptions.find(
                          (option) => option.value === currentStatusFilter
                        )?.label || "All statuses"}
                      </span>
                      <FilterIcon />
                    </button>
                  )}
                  showArrow={false}
                  position="bottom"
                >
                  {statusOptions.map((option) => (
                    <div
                      key={option.value}
                      className="status--dropDown--option"
                      onClick={() => {
                        handleSubStatusChange(option.value);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </SpSelector>
              </div>

              <div className="view-toggle">
                <button
                  className={`view-btn ${
                    selectedView === "compact" ? "active" : ""
                  }`}
                  onClick={() => setSelectedView("compact")}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="4"
                      y="5"
                      width="16"
                      height="2"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="4"
                      y="11"
                      width="16"
                      height="2"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="4"
                      y="17"
                      width="16"
                      height="2"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </button>

                <button
                  className={`view-btn ${
                    selectedView === "detailed" ? "active" : ""
                  }`}
                  onClick={() => setSelectedView("detailed")}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="7"
                      height="7"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="14"
                      y="3"
                      width="7"
                      height="7"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="3"
                      y="14"
                      width="7"
                      height="7"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="14"
                      y="14"
                      width="7"
                      height="7"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </button>

                <button
                  className={`view-btn ${
                    selectedView === "expanded" ? "active" : ""
                  }`}
                  onClick={() => setSelectedView("expanded")}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="3"
                      y="10"
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="3"
                      y="17"
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="10"
                      y="3"
                      width="11"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="10"
                      y="10"
                      width="11"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      x="10"
                      y="17"
                      width="11"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="subCategories__actions">
              <button className="action-btn merge-btn">
                <MergeIcon />
                <span>Merge Sub-Categories</span>
              </button>

              <button
                className="action-btn new-btn"
                onClick={handleAddSubItemClick}
              >
                <Add />
                <span>New Request</span>
              </button>
            </div>
          </div>

          <div className={`subCategories__grid ${selectedView}`}>
            {loadingSub && <p>Loading...</p>}
            {!loadingSub &&
              paginatedSubItems.map((subCategory) => (
                <React.Fragment key={subCategory.id}>
                  {selectedView === "detailed" && (
                    <IndustryCardDetailed
                      item={subCategory}
                      onShowClicked={(item) => onShowClicked(item, true)}
                      onEditClicked={() => handleEditSubItemClick(subCategory)}
                      isShowLoading={isShowLoading}
                      // isEditLoading={isEditLoading}
                    />
                  )}
                  {selectedView === "expanded" && (
                    <IndustryCardExpanded
                      item={subCategory}
                      onShowClicked={(item) => onShowClicked(item, true)}
                      onEditClicked={() => handleEditSubItemClick(subCategory)}
                      onMoveClicked={() => handleMoveSubItemClick(subCategory)}
                      isShowLoading={isShowLoading}
                    />
                  )}
                  {selectedView === "compact" && (
                    <IndustryCardCompact item={subCategory} />
                  )}
                </React.Fragment>
              ))}
          </div>
          {filteredSubItems.length > 0 && (
            <div
              className="colManagement_pagination_footer"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 0",
                width: "100%",
              }}
            >
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={subItemsPerPage}
                  onChange={handleSubItemsPerPageChange}
                  label="Items per page"
                >
                  {viewsPaging[selectedView].map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Pagination
                count={Math.ceil(filteredSubItems.length / subItemsPerPage)}
                page={subItemsPage}
                onChange={handleSubItemsPageChange}
                variant="outlined"
                shape="rounded"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SingleCollection;
